/** Skills (K): class skills with levels, skill detail with the Facet tree, and skill bar editing. */
import { useLayoutEffect, useState } from 'preact/hooks';
import { SKILL_LEVEL_COST, SKILL_MAX_LEVEL, freeSkillPoints, totalSkillPoints } from '../../game/character';
import { registry } from '../../sim/registry';
import { TRIPOD_UNLOCK, resolveSkill } from '../../sim/skills';
import type { SkillDef, TripodDef } from '../../sim/skillTypes';
import { genericIcon, skillIcon } from '../../ui/icons';
import { Window } from '../../ui/app/Window';
import { type Ctx, commit, confirmThen, notify, useCtx } from './common';
import { keyTrap } from './keys';
import {
  BAR_KEYS,
  CAST_TYPE_COLOR,
  CAST_TYPE_LABEL,
  POSITIONAL_LABEL,
  STAGGER_LABEL,
  SUPER_ARMOR_LABEL,
  TIER_COLOR,
  TIER_NUMERAL,
  assignToBar,
  clearBarSlot,
  fmt,
  fmtSec,
  normalizeBar,
  skillCalc,
  swapBarSlots,
} from './logic';
import { SkillTooltip, hideTip, tip } from './Tooltip';

const SKILL_DRAG = 'application/x-af-skill';
const SLOT_DRAG = 'application/x-af-slot';
const TIER_HUE = [0x559ed6, 0x64c53a, 0xe7c669];

type Glyph = Parameters<typeof genericIcon>[2];
const FACET_GLYPHS: [RegExp, Glyph][] = [
  [/mana|frugal/i, 'orb'],
  [/crit/i, 'star'],
  [/attack speed|quick hands|swift/i, 'dash'],
  [/cooldown|quick prep/i, 'spiral'],
  [/shield|less damage|damage reduction|stone skin|iron/i, 'shield'],
  [/bleed|serrat|claw/i, 'claw'],
  [/fire|flame|lava|magma|burn|cinder/i, 'meteor'],
  [/lightning|shock|storm/i, 'bolt'],
  [/stagger|concussion|weak point|sunder|defence/i, 'fist'],
  [/hold|charge|whirl|spin|vortex/i, 'charge'],
  [/area|radius|wide|width|range|distance|stride/i, 'shockwave'],
  [/leap|dash|step|rush|move|pass through/i, 'lance'],
  [/echo|twice|again|second|double|extra/i, 'crescent'],
  [/wrath|frenzy|fury|buff|attack \+|war/i, 'buff'],
];

export function facetIcon(t: TripodDef): string {
  const text = `${t.name} ${t.description}`;
  const glyph = FACET_GLYPHS.find(([re]) => re.test(text))?.[1] ?? 'impact';
  return genericIcon(`facet:${t.id}`, TIER_HUE[t.tier - 1] ?? 0x888888, glyph);
}

function levelUp(ctx: Ctx, id: string, max: boolean): void {
  let n = 0;
  while (ctx.p.levelSkill(id, 1)) {
    n++;
    if (!max) break;
  }
  if (!n) notify(ctx.c.skillLevels[id] === SKILL_MAX_LEVEL ? 'Skill is at its maximum level.' : 'Not enough skill points.', '#e5a24d');
  commit();
}

function levelDown(ctx: Ctx, id: string, min: boolean): void {
  while (ctx.p.levelSkill(id, -1) && min) {
    /* repeat to level 1 */
  }
  commit();
}

function SkillRow({ ctx, def, selected, onSelect, barKey }: { ctx: Ctx; def: SkillDef; selected: boolean; onSelect: () => void; barKey: string | null }) {
  const { c, p, cls } = ctx;
  const lvl = c.skillLevels[def.id] ?? 1;
  const picks = c.tripods[def.id] ?? [];
  const resolved = resolveSkill(def, lvl, picks);
  const ct = resolved.castType;
  const cost = SKILL_LEVEL_COST[lvl + 1] ?? 0;
  const canUp = p.canLevelSkill(def.id);
  return (
    <div
      class={`bsk-row ${selected ? 'is-sel' : ''} ${barKey ? '' : 'is-off'}`}
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      draggable
      onDragStart={(ev) => {
        hideTip();
        ev.dataTransfer?.setData(SKILL_DRAG, def.id);
        if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copyMove';
      }}
      onClick={onSelect}
    >
      <div class="bsk-icon" {...tip(() => <SkillTooltip skillId={def.id} hints={['Drag onto the skill bar']} />)}>
        <img src={skillIcon(def, cls?.hue)} alt="" draggable={false} />
        {barKey && <span class="bsk-key">{barKey}</span>}
      </div>
      <div class="bsk-info">
        <span class="bsk-type" style={{ color: CAST_TYPE_COLOR[ct] }}>
          {CAST_TYPE_LABEL[ct]}
        </span>
        <span class="bsk-name">{def.name}</span>
        <span class="bsk-diamonds" aria-label="Facets">
          {[0, 1, 2].map((ti) => {
            const open = lvl >= (TRIPOD_UNLOCK[ti] ?? 99);
            const on = !!picks[ti] && open;
            return <i key={ti} class={on ? 'on' : open ? 'open' : ''} style={{ '--tc': TIER_COLOR[ti] }} />;
          })}
          {!barKey && <em>not on bar</em>}
        </span>
      </div>
      <div class="bsk-lvl">
        <button
          type="button"
          class="bsk-pm"
          disabled={lvl <= 1}
          aria-label={`Lower ${def.name}`}
          onClick={(ev) => {
            ev.stopPropagation();
            levelDown(ctx, def.id, ev.shiftKey);
          }}
        >
          −
        </button>
        <span class="bsk-lvnum">{lvl}</span>
        <button
          type="button"
          class="bsk-pm bsk-pm--up"
          disabled={!canUp}
          aria-label={`Raise ${def.name}`}
          {...tip(() => <div class="btt-note">{lvl >= SKILL_MAX_LEVEL ? 'Maximum level.' : `Next level: ${cost} skill point${cost === 1 ? '' : 's'}. Shift-click to max.`}</div>)}
          onClick={(ev) => {
            ev.stopPropagation();
            levelUp(ctx, def.id, ev.shiftKey);
          }}
        >
          +
        </button>
      </div>
    </div>
  );
}

function FacetTree({ ctx, def }: { ctx: Ctx; def: SkillDef }) {
  const { c, p } = ctx;
  const lvl = c.skillLevels[def.id] ?? 1;
  const list = registry.tripods.get(def.id) ?? [];
  const picks = c.tripods[def.id] ?? [];
  if (!list.length) return <p class="bsk-empty">This skill has no Facets.</p>;
  return (
    <div class="bsk-tree">
      {[0, 1, 2].map((ti) => {
        const unlock = TRIPOD_UNLOCK[ti] ?? 99;
        const open = lvl >= unlock;
        const opts = list.filter((t) => t.tier === ti + 1);
        if (!opts.length) return null;
        return (
          <div key={ti} class={`bsk-tier ${open ? '' : 'is-locked'}`} style={{ '--tc': TIER_COLOR[ti] }}>
            <div class="bsk-tier-badge" aria-label={`Tier ${TIER_NUMERAL[ti]}`}>
              <span>{TIER_NUMERAL[ti]}</span>
            </div>
            <div class="bsk-opts">
              {opts.map((t) => {
                const on = picks[ti] === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    class={`bsk-opt ${on ? 'is-on' : ''}`}
                    disabled={!open}
                    aria-pressed={on}
                    {...tip(() => (
                      <div class="btt-note">
                        <b style={{ color: TIER_COLOR[ti] }}>
                          {TIER_NUMERAL[ti]} · {t.name}
                        </b>
                        <br />
                        {t.description}
                        {!open && <div class="btt-dim">Unlocks at skill level {unlock}.</div>}
                      </div>
                    ))}
                    onClick={() => {
                      p.setTripod(def.id, ti as 0 | 1 | 2, on ? null : t.id);
                      commit();
                    }}
                  >
                    <span class="bsk-orb">
                      <img src={facetIcon(t)} alt="" />
                    </span>
                    <span class="bsk-opt-text">
                      <b>{t.name}</b>
                      <span>{t.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {!open && <div class="bsk-tier-lock">Unlocks at Skill Lv. {unlock}</div>}
          </div>
        );
      })}
    </div>
  );
}

function SkillDetail({ ctx, def, onAssign, picking }: { ctx: Ctx; def: SkillDef; onAssign: () => void; picking: boolean }) {
  const { c, p, cls } = ctx;
  const lvl = c.skillLevels[def.id] ?? 1;
  const calc = skillCalc(def, lvl, c.tripods[def.id] ?? [], p.build());
  const s = calc.resolved;
  const ct = s.castType;
  const attrs: [string, string][] = [];
  if (s.stagger !== 'none') attrs.push(['Stagger', STAGGER_LABEL[s.stagger]]);
  if ((s.weakPoint ?? 0) > 0) attrs.push(['Weak Point', `Lv. ${s.weakPoint}`]);
  if (s.positional !== 'none') attrs.push(['Attack Type', POSITIONAL_LABEL[s.positional]]);
  if (s.superArmor !== 'none') attrs.push(['Super Armor', SUPER_ARMOR_LABEL[s.superArmor]]);
  if (s.counter) attrs.push(['Counter', 'Yes']);
  return (
    <div class="bsk-detail">
      <div class="bsk-dhead">
        <img class="bsk-dicon" src={skillIcon(def, cls?.hue)} alt="" />
        <div class="bsk-dtitle">
          <div class="bsk-dname">{def.name}</div>
          <div class="bsk-dchips">
            <span style={{ color: CAST_TYPE_COLOR[ct] }}>{CAST_TYPE_LABEL[ct]}</span>
            <span>
              Lv. <b>{lvl}</b>
            </span>
            <span>
              Cooldown <b>{fmtSec(calc.cooldown)}</b>
            </span>
            {calc.mana > 0 && (
              <span class="bsk-mana">
                Mana <b>{calc.mana}</b>
              </span>
            )}
          </div>
        </div>
        <button type="button" class={`btn ${picking ? 'btn--gold' : ''}`} onClick={onAssign}>
          {picking ? 'Choose a slot…' : 'Assign to bar'}
        </button>
      </div>
      <p class="bsk-desc">{def.description}</p>
      {calc.coef > 0 && (
        <div class="bsk-dmg">
          <span>
            Damage <b>{fmt(calc.damage)}</b> <em>({Math.round(calc.coef * 100)}% of Attack Power)</em>
          </span>
          {calc.peak && (
            <span>
              {calc.peak.label} <b>{fmt(calc.peak.damage)}</b> <em>({Math.round(calc.peak.coef * 100)}%)</em>
            </span>
          )}
        </div>
      )}
      {attrs.length > 0 && (
        <div class="bsk-attrs">
          {attrs.map(([k, v]) => (
            <span key={k}>
              {k}: <b>{v}</b>
            </span>
          ))}
        </div>
      )}
      <div class="bsk-tree-h">
        <span class="section-h">Facets</span>
        <span class="bsk-tree-note">Tiers unlock at skill level 4, 7 and 10 · pick one per tier</span>
      </div>
      <FacetTree ctx={ctx} def={def} />
    </div>
  );
}

function BarEditor({ ctx, pick, setPick, onSelect }: { ctx: Ctx; pick: string | null; setPick: (id: string | null) => void; onSelect: (id: string) => void }) {
  const { c, cls } = ctx;
  const bar = normalizeBar(c.bar);
  const [over, setOver] = useState(-1);
  const apexes = cls?.awakenings ?? [];
  const apex = apexes.find((a) => a.id === c.awakening) ?? apexes[0];
  const setBar = (next: (string | null)[]) => {
    c.bar = next;
    commit({ stats: true });
  };
  const slot = (i: number) => {
    const id = bar[i] ?? null;
    const def = id ? registry.skills.get(id) : undefined;
    return (
      <button
        key={i}
        type="button"
        class={`bsk-slot ${over === i ? 'is-over' : ''} ${pick ? 'is-target' : ''} ${def ? '' : 'is-empty'}`}
        aria-label={`Slot ${BAR_KEYS[i]}${def ? `: ${def.name}` : ' (empty)'}`}
        draggable={!!def}
        {...(def ? tip(() => <SkillTooltip skillId={def.id} hints={['Right-click to clear', 'Drag to another slot to swap']} />) : {})}
        onDragStart={(ev) => {
          hideTip();
          ev.dataTransfer?.setData(SLOT_DRAG, String(i));
        }}
        onDragOver={(ev) => {
          const types = ev.dataTransfer?.types ?? [];
          if (types.includes(SKILL_DRAG) || types.includes(SLOT_DRAG)) {
            ev.preventDefault();
            setOver(i);
          }
        }}
        onDragLeave={() => setOver(-1)}
        onDrop={(ev) => {
          ev.preventDefault();
          setOver(-1);
          const sid = ev.dataTransfer?.getData(SKILL_DRAG);
          const from = ev.dataTransfer?.getData(SLOT_DRAG);
          if (sid) setBar(assignToBar(c.bar, sid, i));
          else if (from) setBar(swapBarSlots(c.bar, Number(from), i));
        }}
        onClick={() => {
          if (pick) {
            setBar(assignToBar(c.bar, pick, i));
            setPick(null);
          } else if (id) onSelect(id);
        }}
        onContextMenu={(ev) => {
          ev.preventDefault();
          if (!id) return;
          hideTip();
          setBar(clearBarSlot(c.bar, i));
        }}
      >
        {def && <img src={skillIcon(def, cls?.hue)} alt="" draggable={false} />}
        <span class="bsk-slot-key">{BAR_KEYS[i]}</span>
      </button>
    );
  };
  return (
    <div class="bsk-bar">
      <div class="bsk-apex">
        <div class="bsk-apex-slot" {...(apex ? tip(() => <SkillTooltip skillId={apex.id} />) : {})}>
          {apex && <img src={skillIcon(apex, cls?.hue)} alt="" />}
          <span class="bsk-slot-key">V</span>
        </div>
        <div class="bsk-apex-info">
          <span class="bsk-apex-label">Apex Art</span>
          {apexes.length > 1 ? (
            <div class="bsk-apex-pick" role="radiogroup" aria-label="Apex Art">
              {apexes.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="radio"
                  aria-checked={a.id === apex?.id}
                  class={`bsk-apex-opt ${a.id === apex?.id ? 'is-on' : ''}`}
                  {...tip(() => <SkillTooltip skillId={a.id} />)}
                  onClick={() => {
                    c.awakening = a.id;
                    commit({ stats: true });
                  }}
                >
                  <img src={skillIcon(a, cls?.hue)} alt="" />
                  {a.name}
                </button>
              ))}
            </div>
          ) : (
            <span class="bsk-apex-name">{apex?.name ?? '—'}</span>
          )}
        </div>
      </div>
      <div class="bsk-keys">
        <div class="bsk-keyrow">{[0, 1, 2, 3].map(slot)}</div>
        <div class="bsk-keyrow bsk-keyrow--low">{[4, 5, 6, 7].map(slot)}</div>
      </div>
      <div class="bsk-bar-hint" aria-live="polite">
        {pick ? (
          <>
            <b>{registry.skills.get(pick)?.name}</b>: click a slot or press <kbd>Q</kbd>–<kbd>F</kbd>. <kbd>Esc</kbd> cancels.
          </>
        ) : (
          <>Drag skills onto the bar, or select one and press “Assign to bar”. Right-click a slot to clear it.</>
        )}
      </div>
    </div>
  );
}

const KEY_CODES = ['KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyA', 'KeyS', 'KeyD', 'KeyF'];

export function SkillsPanel() {
  const ctx = useCtx();
  const [selId, setSelId] = useState<string | null>(null);
  const [pick, setPick] = useState<string | null>(null);

  // While choosing a slot, Q–F assign directly and Esc cancels (captured before the game sees them).
  // Registered in a layout effect so it is active before the next key event.
  useLayoutEffect(() => {
    if (!pick) return;
    const onKey = (e: KeyboardEvent) => {
      const i = KEY_CODES.indexOf(e.code);
      if (e.code === 'Escape' || i >= 0) {
        e.preventDefault();
        e.stopPropagation();
        const cur = ctx?.c;
        if (i >= 0 && cur) {
          cur.bar = assignToBar(cur.bar, pick, i);
          commit({ stats: true });
        }
        setPick(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pick, ctx?.c]);

  if (!ctx) return null;
  const { c, cls } = ctx;
  const skills = cls?.skills ?? [];
  const sel = skills.find((s) => s.id === selId) ?? skills[0];
  const bar = normalizeBar(c.bar);
  const free = freeSkillPoints(c);
  const total = totalSkillPoints(c);

  const onListKey = (ev: KeyboardEvent) => {
    if (!sel) return;
    const i = skills.indexOf(sel);
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      const next = skills[Math.max(0, Math.min(skills.length - 1, i + (ev.key === 'ArrowDown' ? 1 : -1)))];
      if (next) setSelId(next.id);
      const list = ev.currentTarget as HTMLElement;
      requestAnimationFrame(() => list.querySelector<HTMLElement>('.bsk-row.is-sel')?.focus());
    } else if (ev.key === '+' || ev.key === '=') levelUp(ctx, sel.id, ev.shiftKey);
    else if (ev.key === '-' || ev.key === '_') levelDown(ctx, sel.id, ev.shiftKey);
  };

  return (
    <Window title="Skills" width={1120} height={740} className="bwin bwin--skills">
      <div class="bsk" {...keyTrap}>
        <header class="bsk-head">
          <div class="bsk-points" {...tip(() => <div class="btt-note">You earn 2 skill points per level; Tonics of Insight, Hearts and Glimmerseeds grant more.</div>)}>
            Skill Points <b>{free}</b>
            <span> / {total}</span>
          </div>
          <span class="bsk-head-hint">Shift-click + to max a skill · Facets unlock at Lv. 4 / 7 / 10</span>
          <button
            type="button"
            class="btn"
            disabled={skills.every((s) => (c.skillLevels[s.id] ?? 1) <= 1)}
            onClick={() =>
              confirmThen('Reset every skill to level 1 and refund all skill points? Facets will be cleared.', 'Reset', () => {
                for (const s of skills) while (ctx.p.levelSkill(s.id, -1)) {
                  /* refund */
                }
                commit();
              })
            }
          >
            Reset All
          </button>
        </header>
        <div class="bsk-main">
          <div
            class="bsk-list"
            role="listbox"
            aria-label="Skills"
            onKeyDown={onListKey}
            onDragOver={(ev) => {
              if (ev.dataTransfer?.types.includes(SLOT_DRAG)) ev.preventDefault();
            }}
            onDrop={(ev) => {
              const from = ev.dataTransfer?.getData(SLOT_DRAG);
              if (!from) return;
              ev.preventDefault();
              c.bar = clearBarSlot(c.bar, Number(from));
              commit({ stats: true });
            }}
          >
            {skills.map((s) => {
              const k = bar.indexOf(s.id);
              return <SkillRow key={s.id} ctx={ctx} def={s} selected={s.id === sel?.id} onSelect={() => setSelId(s.id)} barKey={k >= 0 ? (BAR_KEYS[k] ?? null) : null} />;
            })}
          </div>
          {sel && <SkillDetail ctx={ctx} def={sel} picking={pick === sel.id} onAssign={() => setPick(pick === sel.id ? null : sel.id)} />}
        </div>
        <BarEditor
          ctx={ctx}
          pick={pick}
          setPick={setPick}
          onSelect={(id) => {
            if (skills.some((s) => s.id === id)) setSelId(id);
          }}
        />
      </div>
    </Window>
  );
}
